import { Address, Hex, encodeFunctionData, decodeErrorResult } from 'viem'
import { COMMIT_REVEAL_ABI } from '~/core/abis/commit.reveal.abi'
import { COMMIT_REVEAL_CONTRACT_ADDRESS, RPC_URL } from '~/core/constants'
import { BlinkoGame } from './api'

/**
 * Encodes the settle call for a game — `cashOut` for wins, `markGameAsLost`
 * for losses. Returns null when the required server signature is missing.
 */
const encodeSettleCall = (game: BlinkoGame): Hex | null => {
  const payout = BigInt(game.payoutAmount || '0')
  const isWin = game.status === 'won' || payout > 0n
  try {
    if (isWin) {
      if (!game.cashOutSignature) return null
      return encodeFunctionData({
        abi: COMMIT_REVEAL_ABI,
        functionName: 'cashOut',
        args: [
          game.gameId as Hex,
          payout,
          game.gameStateHash as Hex,
          game.gameSeed as Hex,
          BigInt(game.deadline),
          game.cashOutSignature as Hex,
        ],
      })
    }
    if (!game.markAsLostSignature) return null
    return encodeFunctionData({
      abi: COMMIT_REVEAL_ABI,
      functionName: 'markGameAsLost',
      args: [
        game.gameId as Hex,
        game.gameStateHash as Hex,
        game.gameSeed as Hex,
        BigInt(game.deadline),
        game.markAsLostSignature as Hex,
      ],
    })
  } catch {
    return null
  }
}

/** Pulls the raw revert bytes out of a JSON-RPC error payload, if present. */
const extractRevertData = (rpcError: any): Hex | null => {
  const d = rpcError?.data
  if (typeof d === 'string' && d.startsWith('0x')) return d as Hex
  if (typeof d?.data === 'string' && d.data.startsWith('0x')) return d.data as Hex
  return null
}

/**
 * Simulates each game's settle call (`eth_call`) and returns the set of gameIds
 * (lowercased) that revert with `GameNotActive` — i.e. already settled on-chain
 * while the indexer still lists them as claimable (stale data).
 *
 * Why this exists: the batch settle goes through Multicall3 `aggregate3` with
 * `allowFailure: false`, which reverts with a generic `Error("Multicall3: call
 * failed")`. That top-level message can't tell us *which* game failed or *why*,
 * so on failure we probe each game individually to surface an accurate
 * "already claimed" message instead of a raw stack trace.
 *
 * Best-effort: any network/RPC error for a given game is ignored (treated as
 * "not provably already-claimed").
 */
export const findAlreadyClaimedGames = async (
  games: BlinkoGame[],
  player: Address,
): Promise<Set<string>> => {
  const claimed = new Set<string>()

  await Promise.all(
    games.map(async (game) => {
      const data = encodeSettleCall(game)
      if (!data) return

      try {
        const res = await fetch(RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'eth_call',
            params: [
              { from: player, to: COMMIT_REVEAL_CONTRACT_ADDRESS, data, value: '0x0' },
              'latest',
            ],
          }),
        })
        const json = await res.json()
        const revertData = extractRevertData(json?.error)
        if (!revertData) return

        const decoded = decodeErrorResult({ abi: COMMIT_REVEAL_ABI, data: revertData })
        if (decoded.errorName === 'GameNotActive') {
          claimed.add(game.gameId.toLowerCase())
        }
      } catch {
        // Diagnostic only — ignore decode/network failures.
      }
    }),
  )

  return claimed
}
